import { registerSW } from 'virtual:pwa-register';

/**
 * Service worker registration and update policy.
 *
 * A headset is installed once and then lives in a cupboard, so updates have to
 * arrive on their own. But reloading the app while a class is halfway through
 * a lesson would drop them back to the lobby, and a teacher steering twenty
 * headsets cannot afford that. So: apply an update the moment it is found if
 * the headset is idle, otherwise hold it until the lesson ends.
 *
 * Registration is skipped outside a secure context (plain http over a LAN,
 * say), where service workers are unavailable -- the app then simply runs
 * without offline support rather than throwing on startup.
 */

let applyUpdate: ((reload?: boolean) => Promise<void>) | undefined;
let lessonRunning = false;
let updateWaiting = false;

export type ServiceWorkerStatus = 'offline-ready' | 'update-pending' | 'unsupported';

export function registerServiceWorker(onStatus?: (status: ServiceWorkerStatus) => void): void {
  if (!('serviceWorker' in navigator)) {
    onStatus?.('unsupported');
    return;
  }

  applyUpdate = registerSW({
    onNeedRefresh() {
      updateWaiting = true;
      if (lessonRunning) {
        onStatus?.('update-pending');
        return;
      }
      void applyPendingUpdate();
    },
    onOfflineReady() {
      onStatus?.('offline-ready');
    },
  });
}

/**
 * Tells the updater whether a lesson is on screen. Called when a lesson starts
 * and when the student returns to the lobby.
 */
export function setLessonRunning(running: boolean): void {
  lessonRunning = running;
  if (!running && updateWaiting) void applyPendingUpdate();
}

async function applyPendingUpdate(): Promise<void> {
  if (!updateWaiting || !applyUpdate) return;
  updateWaiting = false;
  await applyUpdate(true);
}

/** True when an update has been downloaded and is waiting for the lesson to end. */
export function isUpdatePending(): boolean {
  return updateWaiting;
}
